/**
 * Itinerary-details feature helpers (formatting, leg pills, offer-state checks).
 *
 * @remarks Colocated with `itineraryDetails/` components; not part of global `src/tools/`.
 */
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { getDelayStatusTextColor } from "../../../theme/DelayStatusColors";
import { resolveItineraryPriceForDisplay } from "../../../tools/ItineraryPricingEvaluationDisplay.tools";
import { getItineraryLegColorHex } from "../../../tools/TransportModeColorsTools";
import {
    getTransportModeGroup,
    getTransportModeGroupForItineraryLeg,
    isTransitLikeTransportMode,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { RentalReturnConstraint, type ItineraryDTO, type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { SegmentationProviderId } from "../../../types/itinerary/segmentation-provider.enum";

/** Five-minute window used to round BlaBlaDaily departure labels in the leg timeline. */
export const FIVE_MINUTES_IN_MS = 5 * 60000;

/**
 * Whether an itinerary leg mode should be treated as public transit in itinerary-details UI.
 *
 * @param mode - Raw leg `mode` string from the itinerary DTO.
 * @returns `true` when the mode maps to a transit-like transport group.
 */
export const isTransitLegMode = ( mode: string ): boolean => {
    return isTransitLikeTransportMode( mode );
};

/**
 * Whether a leg uses the simplified walk / bicycle / car / my-car timeline row
 * (mode picto, colored vertical connector, label, optional departure time).
 *
 * @param mode - Raw leg `mode` string from the itinerary DTO.
 * @returns `true` for walk, personal bicycle, car, and my-car legs.
 */
export const isSimpleMobilityLegMode = ( mode: string ): boolean => {
    const group = getTransportModeGroup( mode );

    return group === TransportModeGroup.WALK ||
      group === TransportModeGroup.BICYCLE ||
      group === TransportModeGroup.CAR ||
      group === TransportModeGroup.MY_CAR;
};

/**
 * Formats epoch milliseconds as a 24-hour clock label for itinerary-details rows.
 *
 * @param timeMs - Epoch milliseconds.
 * @returns Localized `HH:mm` string.
 */
export const formatItineraryTime = ( timeMs: number ): string => {
    const date = new Date( timeMs );

    return date.toLocaleTimeString( [], {
        "hour":   "2-digit",
        "minute": "2-digit",
        "hour12": false,
    } );
};

/**
 * Whole minutes elapsed between two timestamps, floored at zero.
 *
 * @param fromMs - Start epoch milliseconds.
 * @param toMs - End epoch milliseconds.
 * @returns Non-negative minute count.
 */
export const minutesBetween = ( fromMs: number, toMs: number ): number => {
    return Math.max( 0, Math.floor( ( toMs - fromMs ) / ( 1000 * 60 ) ) );
};

/**
 * Human-readable transfer wait copy between two legs.
 *
 * @param fromMs - Arrival epoch milliseconds of the previous leg.
 * @param toMs - Departure epoch milliseconds of the next leg.
 * @returns Label such as `12 minutes`.
 */
export const formatTransferDuration = ( fromMs: number, toMs: number ): string => {
    return `${ minutesBetween( fromMs, toMs ) } minutes`;
};

/**
 * Hex color for a itinerary-details leg pill border and accent.
 *
 * @param leg - Itinerary leg transport signals (`mode`, `type`, `rental`).
 * @returns CSS hex color from the shared transport-mode palette.
 */
export const getItineraryDetailsLegPillColor = ( leg: Pick<ItineraryLegDTO, "mode" | "type" | "rental"> ): string => {
    return getItineraryLegColorHex( leg );
};

/**
 * Type guard for non-empty, non-whitespace display strings.
 *
 * @param value - Candidate label or name.
 * @returns `true` when `value` is a trimmed non-empty string.
 */
export const hasNonEmptyText = ( value: string | null | undefined ): value is string => {
    return value != null && value.trim().length > 0;
};

/**
 * Departure time label for a leg title row in itinerary details.
 *
 * @remarks BlaBlaDaily legs show a five-minute rounded window (`HH:mm - HH:mm`); other modes
 * show a single `HH:mm` value.
 *
 * @param departureTimeIso - ISO datetime from the leg step, when present.
 * @param modeGroup - Resolved transport mode group for the leg.
 * @returns Formatted label, or an empty string when the datetime is missing or invalid.
 */
export const formatLegDepartureTimeLabel = (
    departureTimeIso: string | undefined,
    modeGroup: TransportModeGroup,
): string => {
    if ( departureTimeIso == null ) {
        return "";
    }

    const departureMs = new Date( departureTimeIso ).getTime();
    if ( Number.isNaN( departureMs ) ) {
        return "";
    }

    if ( modeGroup === TransportModeGroup.BLABLADAILY ) {
        const roundedStart = Math.floor( departureMs / FIVE_MINUTES_IN_MS ) * FIVE_MINUTES_IN_MS;
        const roundedEnd = Math.ceil( departureMs / FIVE_MINUTES_IN_MS ) * FIVE_MINUTES_IN_MS;

        return `${ formatItineraryTime( roundedStart ) } - ${ formatItineraryTime( roundedEnd ) }`;
    }

    return formatItineraryTime( departureMs );
};

/**
 * Transit line short name shown in leg pills and compact timeline chips.
 *
 * @param leg - Itinerary leg with optional `route.shortName`.
 * @returns Route short name, or an empty string when absent.
 */
export const getLegRouteShortName = ( leg: ItineraryLegDTO ): string => {
    return leg.route?.shortName ?? "";
};

/** Suffix appended when a rental provider pill label is shortened. */
const RENTAL_PROVIDER_PILL_ELLIPSIS = "...";

/** Maximum characters kept in a rental provider leg pill before ellipsis. */
const RENTAL_PROVIDER_LEG_PILL_MAX_CHARS = 15;

/** Number of leading words kept when shortening a rental provider leg pill label. */
const RENTAL_PROVIDER_LEG_PILL_WORD_COUNT = 2;

/**
 * Shortens a rental provider name for a pill using a max length and leading word count.
 * The ellipsis suffix is only added when the original name exceeds `maxChars`.
 *
 * @param providerName - Raw rental provider display name.
 * @param maxChars - Maximum characters kept from the shortened candidate (before ellipsis).
 * @param wordCount - Number of leading words to keep when shortening by words.
 * @returns Pill-safe label, with `...` when truncated.
 */
const formatRentalProviderPillLabel = (
    providerName: string,
    maxChars: number,
    wordCount: number,
): string => {
    const trimmedName = providerName.trim();

    if ( trimmedName.length <= maxChars ) {
        return trimmedName;
    }

    const words = trimmedName.split( /\s+/ ).filter( ( word ) => word.length > 0 );
    const candidate = words.slice( 0, wordCount ).join( " " ) || trimmedName;

    if ( candidate.length <= maxChars ) {
        return `${ candidate }${ RENTAL_PROVIDER_PILL_ELLIPSIS }`;
    }

    return `${ candidate.slice( 0, maxChars ) }${ RENTAL_PROVIDER_PILL_ELLIPSIS }`;
};

/**
 * Shortens a rental provider name for the itinerary-details leg pill (15 chars, first two words).
 *
 * @param providerName - Raw rental provider display name.
 * @returns Pill-safe label.
 */
export const formatRentalProviderLegPillLabel = ( providerName: string ): string => {
    return formatRentalProviderPillLabel(
        providerName,
        RENTAL_PROVIDER_LEG_PILL_MAX_CHARS,
        RENTAL_PROVIDER_LEG_PILL_WORD_COUNT,
    );
};

/**
 * Returns the text shown inside the itinerary-details leg pill for a given leg.
 *
 * @param leg - Itinerary leg (transit line short name or formatted rental provider name).
 * @returns Pill label, or an empty string when nothing should be rendered.
 */
export const getItineraryDetailsLegPillLabel = ( leg: ItineraryLegDTO ): string => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );

    if ( modeGroup === TransportModeGroup.RENTAL ) {
        const providerName = leg.rental?.provider?.name ?? leg.route?.shortName ?? "";

        if ( !hasNonEmptyText( providerName ) ) {
            return "";
        }

        return formatRentalProviderLegPillLabel( providerName );
    }

    return getLegRouteShortName( leg );
};

/** Warning copy shown when a rental vehicle must be returned at the pickup location. */
export const RENTAL_ORIGIN_STATION_RETURN_WARNING_MESSAGE =
    "Le véhicule doit être rendu à l'endroit où il a été pris.";

/** Dark orange used for the origin-station rental return warning (icon and text). */
export const RENTAL_ORIGIN_STATION_RETURN_WARNING_COLOR = "#c2410c";

/** Notice copy shown on on-demand mobility (ODM) legs in itinerary details. */
export const ODM_MOBILITY_NOTICE_MESSAGE = "Mobilité à la demande";

/** Accent color for the ODM mobility notice (icon and text). */
export const ODM_MOBILITY_NOTICE_COLOR = RENTAL_ORIGIN_STATION_RETURN_WARNING_COLOR;

/**
 * Whether an itinerary leg uses the on-demand mobility (ODM) provider mode.
 *
 * @param mode - Raw leg `mode` string from the itinerary DTO.
 * @returns `true` when the normalized mode is `odm`.
 */
export const isOdmItineraryLegMode = ( mode: string ): boolean => {
    return mode.trim().toLowerCase() === "odm";
};

/**
 * Whether to show the origin-station rental return warning below leg duration.
 *
 * @param leg - Itinerary leg.
 * @returns `true` when the rental leg requires return at the pickup station.
 */
export const shouldShowRentalOriginStationReturnWarning = ( leg: ItineraryLegDTO ): boolean => {
    if ( getTransportModeGroupForItineraryLeg( leg ) !== TransportModeGroup.RENTAL ) {
        return false;
    }

    return leg.rental?.returnConstraint === RentalReturnConstraint.ORIGIN_STATION;
};

/**
 * Text color for realtime vs scheduled departure/arrival times in leg details.
 *
 * @param isDarkUi - Whether the itinerary-details panel uses the dark palette.
 * @param realtimeIso - Observed ISO datetime from realtime data, when available.
 * @param scheduledIso - Planned ISO datetime from the itinerary leg.
 * @returns Theme delay-status color when both timestamps are valid; otherwise secondary overlay text color.
 */
export const getRealtimeStatusTextColor = (
    isDarkUi: boolean,
    realtimeIso: string | undefined,
    scheduledIso: string | undefined,
): string => {
    const palette = getUIColorPalette( isDarkUi );
    if ( realtimeIso === undefined || scheduledIso === undefined ) {
        return palette.overlayTextSecondary;
    }
    const realtimeMs = new Date( realtimeIso ).getTime();
    const scheduledMs = new Date( scheduledIso ).getTime();
    if ( Number.isNaN( realtimeMs ) || Number.isNaN( scheduledMs ) ) {
        return palette.overlayTextSecondary;
    }

    return getDelayStatusTextColor( isDarkUi, ( realtimeMs - scheduledMs ) / 1000 );
};

/**
 * Resolves the idle itinerary price shown in itinerary details when offer evaluation is not active.
 *
 * @remarks Delegates to {@link resolveItineraryPriceForDisplay} so GA Swiss CFF adjustments stay centralized.
 *
 * @param itinerary - Loaded itinerary.
 * @param pricingPreferences - Active pricing preferences.
 * @returns Display price in CHF, or `null` when absent.
 */
export const resolveItineraryDetailsIdleItineraryPrice = (
    itinerary: ItineraryDTO,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): number | null => {
    return resolveItineraryPriceForDisplay( itinerary, pricingPreferences );
};

/**
 * Whether the itinerary's last leg is not walk-only (controls the pre-arrival dash connector).
 *
 * @param itinerary - Loaded itinerary with legs.
 * @returns `true` when the last leg exists and is not grouped as walk.
 */
export const itineraryEndsWithNonWalkLeg = ( itinerary: ItineraryDTO ): boolean => {
    const lastLeg = itinerary.legs[itinerary.legs.length - 1];
    if ( lastLeg === undefined ) {
        return false;
    }

    return getTransportModeGroupForItineraryLeg( lastLeg ) !== TransportModeGroup.WALK;
};

/**
 * Detects itinerary-wide incomplete-offer state: at least one {@link ItineraryDTO.pricingSegments} row
 * maps to a segment sales row whose `providerOffers` is `null` (no provider execution or global pricing failure).
 *
 * @param itinerary - Loaded itinerary, including optional `offer.segments`.
 * @param options - Optional pricing snapshot; when `swissReductionCard` is `"ga"`, Swiss CFF segments are
 * ignored because the UI treats them as fully covered (see `offerPricing/OfferPricingFacade`).
 * @returns `true` when any mapped segment exists and has `providerOffers === null`.
 */
export const itineraryHasNullSegmentProviderOffers = (
    itinerary: ItineraryDTO,
    options?: { readonly "swissReductionCard"?: ItineraryPricingPreferencesDTO["swissReductionCard"] },
): boolean => {
    const pricingSegments = itinerary.pricingSegments;
    if ( pricingSegments === undefined || pricingSegments.length === 0 ) {
        return false;
    }

    const offerSegments = itinerary.offer?.segments;
    if ( offerSegments === undefined ) {
        return false;
    }

    const ignoreSwissCffNullOffers = options?.swissReductionCard === "ga";

    return pricingSegments.some( ( segment ) => {
        if ( ignoreSwissCffNullOffers === true && segment.provider?.id === SegmentationProviderId.swiss_cff ) {
            return false;
        }

        const segmentSales = offerSegments[segment.id];

        return segmentSales != null && segmentSales.providerOffers === null;
    } );
};

/**
 * Toggles itinerary leg selection when the same leg is clicked again.
 *
 * @param selectedLeg Currently selected leg, if any.
 * @param leg Leg that was clicked.
 * @param setSelectedLeg Home-level setter for the highlighted leg.
 */
export function toggleSelectedItineraryLeg(
    selectedLeg: ItineraryLegDTO | null,
    leg: ItineraryLegDTO,
    setSelectedLeg: ( leg: ItineraryLegDTO | null ) => void,
): void {
    if ( selectedLeg !== null && selectedLeg.id === leg.id ) {
        setSelectedLeg( null );

        return;
    }

    setSelectedLeg( leg );
}
